// How a planner card shows its colour. One answer for the Assign Board, the
// library and the running order, so the three never drift. Andrew, 2026-10-12
// (planner mockup v6): "Color by" says what the colour MEANS (dance style,
// teacher, level) and the "look" says how it SHOWS (stripe, outline, dot, full).

export const COLOR_BY_LABELS = { none: 'None', style: 'Dance style', teacher: 'Teacher', level: 'Level' };
export const COLOR_LOOK_LABELS = { stripe: 'Stripe', outline: 'Outline', dot: 'Dot', fill: 'Full card' };

// Names on a class and names in the studio profile do not always match letter
// for letter ("Hip Hop" vs "Hip-Hop", a trailing space, a renamed style), so
// the lookup ignores case, spaces and punctuation. Andrew, 2026-10-12 on
// staging: "some work and some dont".
const fold = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const lookup = (name, colorMap) => {
    if (!name) return null;
    if (colorMap[name]) return colorMap[name];
    const key = fold(name);
    if (!key) return null;
    const hit = Object.keys(colorMap).find((k) => fold(k) === key);
    return hit ? colorMap[hit] : null;
};

const byStyle   = (cls, colorMap) => (cls.styles   || []).map((n) => lookup(n, colorMap)).find(Boolean) || null;
const byTeacher = (cls, colorMap) => (cls.teachers || []).map((n) => lookup(n, colorMap)).find(Boolean) || null;
const byLevel   = (cls)           => (cls.levels   || []).find(Boolean) || null; // level values are colour strings

// The colour a class carries under the chosen "Color by". The chosen one wins;
// if the class has nothing under it, the others fill in (the board always did
// this, and a blank card next to coloured ones reads as a glitch). None means
// none: no colour at all.
export function resolveColor(cls, colorBy, colorMap = {}) {
    if (!cls || !colorBy || colorBy === 'none') return null;
    const order = {
        style:   [byStyle, byTeacher, byLevel],
        teacher: [byTeacher, byStyle, byLevel],
        level:   [byLevel, byStyle, byTeacher],
    }[colorBy];
    if (!order) return null;
    for (const fn of order) {
        const c = fn(cls, colorMap);
        if (c) return c;
    }
    return null;
}

// Style fragments for a card. `card` spreads onto the card; `marker` is the
// colour for a dot or number badge (null when the look does not use one).
// With no colour (Color by: None, or a class with nothing to colour by) the
// card stays plain: a grey stripe on everything just reads as dull.
export function lookStyles(color, look = 'stripe') {
    const c = color || null;
    if (!c) return { card: {}, marker: null };
    switch (look) {
        case 'outline':
            return { card: { border: `1.5px solid ${c}` }, marker: null };
        case 'dot':
            return { card: {}, marker: c };
        case 'fill':
            // color-mix works for hex AND hsl()/rgb() strings; "#abc" + "22" only
            // works for hex, and level colours are hsl(). Andrew, 2026-10-12:
            // "Full card nothing gets highlighted".
            return { card: { background: `color-mix(in srgb, ${c} 16%, white)`, border: `1px solid color-mix(in srgb, ${c} 40%, white)` }, marker: null };
        case 'stripe':
        default:
            return { card: { borderLeft: `4px solid ${c}` }, marker: null };
    }
}
